import { motion } from "framer-motion";
import { useLanguage } from "@/i18n";

// Hero image for Projects page
const PROYECTOS_HERO = "https://customer-assets.emergentagent.com/job_export-to-wp/artifacts/84hn7wr7_Proyectos%20ficcio%CC%81n%20IA.jpg";

// Background images
const BG_FICTION = "https://customer-assets.emergentagent.com/job_export-to-wp/artifacts/nxjh1heq_freepik__make-a-wallpaper-with-the-the-exact-floor-and-grad__60229.jpeg";
const BG_AVATAR = "https://customer-assets.emergentagent.com/job_export-to-wp/artifacts/iw7g8g2v_fons%20dolores.jpeg";
const BG_MUSIC = "https://customer-assets.emergentagent.com/job_export-to-wp/artifacts/fdar7f2q_fons%20musica.jpeg";

// Award images
const AWARD_SOC_BARCELONA = "https://customer-assets.emergentagent.com/job_export-to-wp/artifacts/muvube5b_freepik__design-a-professional-film-festival-award-laurel-f__56511.png";
const AWARD_PU_DRENPA = "https://customer-assets.emergentagent.com/job_export-to-wp/artifacts/crsvca7b_freepik__design-a-professional-film-festival-award-laurel-f__56512.png";

// Section posters
const AVATAR_POSTER = "https://customer-assets-0z36b82j.emergentagent.net/job_5258f97a-514c-45b1-a984-43e415ab135d/artifacts/yh24cqnl_Imagen%20de%20Codex%2024%20sept%202026%2C%2009_53_42.png";
const MUSIC_POSTER = "https://customer-assets.emergentagent.com/job_export-to-wp/artifacts/5lnn94t5_Roger_Delmont_0b97a3be-b219-4dd8-8b75-3f232bfc6950_upscaled%20%281%29.jpg";

// Ficción Real section
const FICCION_REAL_POSTER = "https://customer-assets.emergentagent.com/job_21a64dd8-89a2-456a-af50-8a7c7a229e04/artifacts/whc3d0j7_Ficcion%20real.jpeg";
const FICCION_REAL_IMAGE = "https://customer-assets.emergentagent.com/job_21a64dd8-89a2-456a-af50-8a7c7a229e04/artifacts/6wfc53vq_Ficcion%20real.jpeg";
const POR_UNA_NUBE_POSTER = "https://customer-assets.emergentagent.com/job_21a64dd8-89a2-456a-af50-8a7c7a229e04/artifacts/rh5eb2fy_freepik__redo-the-film-poster-to-look-professional-arty-and__51073.jpeg";

// Por una nube - Gallery images
const GALLERY_IMG_1 = "https://customer-assets.emergentagent.com/job_21a64dd8-89a2-456a-af50-8a7c7a229e04/artifacts/9mvwxecd_1Finish%20line.jpeg";
const GALLERY_IMG_2 = "https://customer-assets.emergentagent.com/job_21a64dd8-89a2-456a-af50-8a7c7a229e04/artifacts/ah72pi5l_2.jpg";
const GALLERY_IMG_3 = "https://customer-assets.emergentagent.com/job_21a64dd8-89a2-456a-af50-8a7c7a229e04/artifacts/c3z6hec8_3.jpg";
const AWARDS_POSTER = "https://customer-assets.emergentagent.com/job_21a64dd8-89a2-456a-af50-8a7c7a229e04/artifacts/srnkdx5z_Poster.jpeg";

// Kintsukuroi section
const KINTSUKUROI_POSTER = "https://customer-assets.emergentagent.com/job_21a64dd8-89a2-456a-af50-8a7c7a229e04/artifacts/xnjkdprd_freepik__enhance__57694.png";

// Helper to convert YouTube URLs to embed format
const getEmbedUrl = (url) => {
  if (url.includes('youtube.com/watch')) {
    const videoId = url.split('v=')[1]?.split('&')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  if (url.includes('youtube.com/shorts')) {
    const videoId = url.split('shorts/')[1]?.split('?')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  if (url.includes('youtu.be')) {
    const videoId = url.split('youtu.be/')[1]?.split('?')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  return url;
};

// Video Player Component
const VideoPlayer = ({ url, className = "" }) => {
  const embedUrl = getEmbedUrl(url);
  return (
    <div className={`relative aspect-video bg-black/50 rounded-sm overflow-hidden shadow-xl ${className}`}>
      <iframe
        src={embedUrl}
        title="Video"
        className="absolute inset-0 w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
};

// Vertical Video Player (9:16 - shown full, centered, not cropped)
const VerticalVideoPlayer = ({ url, className = "" }) => {
  const embedUrl = getEmbedUrl(url);
  return (
    <div className={`flex justify-center ${className}`}>
      <div className="relative aspect-[9/16] w-full max-w-[220px] sm:max-w-[240px] bg-black/50 rounded-sm overflow-hidden shadow-xl">
        <iframe
          src={embedUrl}
          title="Video"
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      </div>
    </div>
  );
};

// Video Pair Component (Two videos side by side)
const VideoPair = ({ leftVideo, leftText, rightVideo, rightText, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, delay }}
    className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12"
  >
    {/* Left Video */}
    <div>
      <VideoPlayer url={leftVideo} />
      <p className="mt-4 text-sm lg:text-base text-foreground/80 leading-relaxed whitespace-pre-line">
        {leftText}
      </p>
    </div>
    {/* Right Video */}
    <div>
      <VideoPlayer url={rightVideo} />
      <p className="mt-4 text-sm lg:text-base text-foreground/80 leading-relaxed whitespace-pre-line">
        {rightText}
      </p>
    </div>
  </motion.div>
);

// Award Project Component
const AwardProject = ({ awardImage, videoUrl, text, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, delay }}
    className="flex flex-col items-center"
  >
    <img 
      src={awardImage} 
      alt="Award" 
      className="w-40 lg:w-48 h-auto mb-6"
      loading="lazy"
    />
    <VideoPlayer url={videoUrl} className="w-full" />
    <p className="mt-4 text-sm lg:text-base text-foreground/80 leading-relaxed text-center whitespace-pre-line">
      {text}
    </p>
  </motion.div>
);

// Background Section Component
const BackgroundSection = ({ bgImage, children, className = "", blendTop = false, blendBottom = false }) => (
  <section className={`relative ${className}`}>
    {/* Background with blur */}
    <div className="absolute inset-0 overflow-hidden">
      <img
        src={bgImage}
        alt=""
        className="w-full h-full object-cover"
        style={{ filter: 'blur(8px)', transform: 'scale(1.05)' }}
        loading="lazy"
      />
      {/* Overlay for readability */}
      <div className="absolute inset-0 bg-background/60" />
      {/* Top gradient blend */}
      {blendTop && (
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-background to-transparent" />
      )}
      {/* Bottom gradient blend */}
      {blendBottom && (
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      )}
    </div>
    {/* Content */}
    <div className="relative z-10">
      {children}
    </div>
  </section>
);

const ProyectosPage = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[50vh] lg:h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={PROYECTOS_HERO}
            alt={t.proyectos.heroTitle}
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        </div>
        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-12 pb-12 lg:pb-16">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-wide text-foreground"
          >
            {t.proyectos.heroTitle}
          </motion.h1>
        </div>
      </section>

      {/* SECTION 1-5: Fiction Projects with PHOTO 1 Background */}
      <BackgroundSection bgImage={BG_FICTION} blendTop={true} blendBottom={true}>
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-24 space-y-20 lg:space-y-28">
          
          {/* Introductory Quote */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-serif text-xl lg:text-2xl font-light text-foreground/80 leading-relaxed italic text-center max-w-4xl mx-auto"
          >
            {t.proyectos.introQuote}
          </motion.p>

          {/* NEW FEATURED VIDEOS: SHELTER & THE DEVOURS */}
          <VideoPair
            leftVideo="https://youtu.be/RbauJ0GS2f0"
            leftText={t.proyectos.shelter}
            rightVideo="https://youtu.be/FxNv7zb1FHw"
            rightText={t.proyectos.theDevours}
            delay={0.1}
          />

          {/* SECTION 2: PAIR 1 — Pedro Mosquito + Eusebio Merlay */}
          <VideoPair
            leftVideo="https://youtu.be/guEcAdwIfnw"
            leftText={t.proyectos.pedroMosquito}
            rightVideo="https://youtu.be/X-VfadU_1lo"
            rightText={t.proyectos.eusebioMerlay}
            delay={0.1}
          />

          {/* SECTION 2: PAIR 2 — Skate Around the World + My Last Exorcism */}
          <VideoPair
            leftVideo="https://youtu.be/Wo4wbGzqp8M"
            leftText={t.proyectos.skateAroundWorld}
            rightVideo="https://youtu.be/agh2QgYE1vI?si=IPTS4tO8ecNsmmXN"
            rightText={t.proyectos.myLastExorcism}
            delay={0.1}
          />

          {/* SECTION 3: AWARDS PROJECTS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
            <AwardProject
              awardImage={AWARD_SOC_BARCELONA}
              videoUrl="https://www.youtube.com/shorts/blQMcbIqm8s"
              text={t.proyectos.socBarcelona}
              delay={0.1}
            />
            <AwardProject
              awardImage={AWARD_PU_DRENPA}
              videoUrl="https://www.youtube.com/shorts/44d032uCgXc"
              text={t.proyectos.puDrenpa}
              delay={0.2}
            />
          </div>

          {/* SECTION 4: FICTION PROJECTS */}
          <VideoPair
            leftVideo="https://www.youtube.com/watch?v=Krm9rXwExOo"
            leftText={t.proyectos.unthinkableAlien}
            rightVideo="https://www.youtube.com/watch?v=C5eaR6xZQ0s"
            rightText={t.proyectos.woodyAlien}
            delay={0.1}
          />

          {/* SECTION 5: HYBRID PROJECT */}
          <div className="space-y-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-serif text-2xl lg:text-3xl font-light text-foreground text-center"
            >
              {t.proyectos.hybridProject}
            </motion.h2>

            {/* Two videos side by side */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
            >
              <VideoPlayer url="https://youtu.be/tcgNaY-kL_E?si=DzVRvY8Lk4XNrzre" />
              <VideoPlayer url="https://youtu.be/3Fw6UiYmeow?si=kCcYjHtHocVxM54T" />
            </motion.div>

            {/* Centered video with split text */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="max-w-3xl mx-auto"
            >
              <VideoPlayer url="https://youtu.be/KoBlZ2EHudw?si=uluXcFcxykyUxeE-" />
              <div className="mt-4 flex justify-between items-start">
                <p className="text-sm lg:text-base text-foreground/80">{t.proyectos.chatGptEngana}</p>
                <p className="text-sm lg:text-base text-foreground/80 text-right">{t.proyectos.miniSerieHumor}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </BackgroundSection>

      {/* SECTION 6: Avatar Poster with Blend */}
      <section className="relative">
        {/* Gradient blend from fiction background */}
        <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-background/80 to-transparent z-10" />
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <img
            src={AVATAR_POSTER}
            alt="AI Influencers: Dolores Rainbow, El Tremendista and Greg Bucka"
            className="w-full h-auto"
            loading="lazy"
          />
          {/* Bottom gradient */}
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent" />
        </motion.div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 py-12 text-center -mt-8">
          <p className="text-base lg:text-lg text-foreground/80 leading-relaxed whitespace-pre-line">
            {t.proyectos.avatarText}
          </p>
        </div>
      </section>

      {/* SECTION 7: Avatar Projects with PHOTO 2 Background */}
      <BackgroundSection bgImage={BG_AVATAR} blendTop={true} blendBottom={true}>
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-24 space-y-12 lg:space-y-16">
          {/* Dolores Rainbow - Game Tester Avatar (horizontal) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="max-w-3xl mx-auto"
          >
            <VideoPlayer url="https://www.youtube.com/watch?v=UPvdsbZiJZA&t=33" />
            <p className="mt-4 text-center text-sm lg:text-base text-foreground/80 leading-relaxed whitespace-pre-line">
              {t.proyectos.doloresRainbowGame}
            </p>
          </motion.div>

          {/* El Tremendista + Greg Bucka (vertical shorts 9:16) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="grid grid-cols-2 gap-6 sm:gap-10 max-w-2xl mx-auto"
          >
            <div>
              <VerticalVideoPlayer url="https://www.youtube.com/embed/3Dm7BM_FNwA" />
              <p className="mt-4 text-center text-sm lg:text-base text-foreground/80 leading-relaxed whitespace-pre-line">
                {t.proyectos.elTremendista}
              </p>
            </div>
            <div>
              <VerticalVideoPlayer url="https://www.youtube.com/embed/LX3Bf4RXNRU" />
              <p className="mt-4 text-center text-sm lg:text-base text-foreground/80 leading-relaxed whitespace-pre-line">
                {t.proyectos.gregBucka}
              </p>
            </div>
          </motion.div>
        </div>
      </BackgroundSection>

      {/* SECTION 8: Music Videos Poster */}
      <section className="relative">
        {/* Gradient blend */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-background/80 to-transparent z-10" />
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <img
            src={MUSIC_POSTER}
            alt={t.proyectos.musicVideosTitle}
            className="w-full h-auto"
            loading="lazy"
          />
          {/* Title positioned absolutely - aligned with header logo left edge, in the water area */}
          <div className="absolute bottom-[15%] left-0 w-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-foreground drop-shadow-lg"
                  style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}>
                {t.proyectos.musicVideosTitle}
              </h2>
            </div>
          </div>
          {/* Bottom gradient */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
        </motion.div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 py-8 text-center -mt-16">
          <p className="text-base lg:text-lg text-foreground/80 leading-relaxed whitespace-pre-line">
            {t.proyectos.musicVideosText}
          </p>
        </div>
      </section>

      {/* SECTION 9: Music Videos with PHOTO 3 Background */}
      <BackgroundSection bgImage={BG_MUSIC} blendTop={true} blendBottom={true} className="pb-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12 lg:py-16 space-y-12 lg:space-y-16">
          
          {/* STILLNESS IN THE DUST */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <VideoPlayer url="https://youtu.be/L7t5GykJMK0" />
            <p className="mt-6 text-center text-base lg:text-lg text-foreground/80 leading-relaxed whitespace-pre-line">
              {t.proyectos.stillnessInTheDust}
            </p>
          </motion.div>

          {/* LEAP OF FAITH */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <VideoPlayer url="https://youtu.be/PntkX1uMH2U" />
            <p className="mt-6 text-center text-base lg:text-lg text-foreground/80 leading-relaxed whitespace-pre-line">
              {t.proyectos.leapOfFaith}
            </p>
          </motion.div>

          {/* Featured Video (Centered, Larger) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <VideoPlayer url="https://youtu.be/J-CXMY038vE?si=t8mQf6sTl0nNqCRC" />
            <p className="mt-6 text-center text-base lg:text-lg text-foreground/80 leading-relaxed whitespace-pre-line">
              {t.proyectos.fionaMont}
            </p>
          </motion.div>

          {/* Orthodox Project - Video + Text Below */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            {/* Video - Centered, same size as Fiona Mont video above */}
            <VideoPlayer url="https://youtu.be/B27cFK26z5M" />
            {/* Text - Immediately below video, centered */}
            <p className="mt-4 text-center text-sm lg:text-base text-foreground/80 leading-relaxed whitespace-pre-line">
              {t.proyectos.orthodox}
            </p>
          </motion.div>
        </div>
      </BackgroundSection>

      {/* SECTION 10: Ficción Real - Full-width image section header */}
      <section className="relative w-full">
        {/* Gradient blend from previous section */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-background/80 to-transparent z-10" />
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative w-full"
        >
          <img
            src={FICCION_REAL_IMAGE}
            alt={t.proyectos.ficcionReal}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
          {/* Title positioned using EXACT same alignment as "Ficción con IA" and "Music Videos" */}
          <div className="absolute bottom-[15%] left-0 w-full">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-foreground drop-shadow-lg"
                  style={{ textShadow: '0 2px 20px rgba(0,0,0,0.7)' }}>
                {t.proyectos.ficcionReal}
              </h2>
            </div>
          </div>
          {/* Bottom gradient */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
        </motion.div>
      </section>

      {/* SECTION 11: Por una nube - Feature Film Project with Ficcion real.jpeg background */}
      <BackgroundSection bgImage={FICCION_REAL_POSTER} blendTop={true} blendBottom={true} className="pb-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12 lg:py-16 space-y-12 lg:space-y-16">
          
          {/* Project Intro + Title Hierarchy - Combined for tighter spacing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="text-lg lg:text-xl text-foreground/80 mb-2">
              {t.proyectos.featureFilmProject}
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-foreground mb-3">
              Por una nube (A Cloud)
            </h2>
            <p className="text-base lg:text-lg text-foreground/70">
              {t.proyectos.featureFilmDrama}
            </p>
          </motion.div>

          {/* Poster + Logline + Temas Layout */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-start"
          >
            {/* Poster */}
            <div>
              <img
                src={POR_UNA_NUBE_POSTER}
                alt="Por una nube - Poster"
                className="w-full h-auto rounded-sm shadow-2xl"
                loading="lazy"
              />
            </div>
            {/* Logline + Temas - Vertically centered with poster */}
            <div className="flex flex-col justify-center h-full">
              <p className="text-sm lg:text-base text-foreground/80 leading-relaxed mb-6">
                <span className="font-medium text-foreground">{t.proyectos.loglineLabel}</span> {t.proyectos.loglineText}
              </p>
              <div className="text-foreground/90 leading-relaxed">
                <span className="font-medium text-foreground block mb-3">{t.proyectos.themesLabel}</span>
                {t.proyectos.porUnaNubeThemes.map((theme, index) => (
                  <span key={index} className="block ml-4">• {theme}</span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Image Row - Three images, reduced size (~90%), no cropping, order: 2.jpg, 1Finish line, 3.jpg */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center gap-4 w-full"
          >
            <div className="w-[28%] rounded-sm overflow-hidden">
              <img
                src={GALLERY_IMG_2}
                alt="Por una nube - Award 1"
                className="w-full h-auto object-contain"
                loading="lazy"
              />
            </div>
            <div className="w-[28%] rounded-sm overflow-hidden">
              <img
                src={GALLERY_IMG_1}
                alt="Por una nube - Award 2"
                className="w-full h-auto object-contain"
                loading="lazy"
              />
            </div>
            <div className="w-[28%] rounded-sm overflow-hidden">
              <img
                src={GALLERY_IMG_3}
                alt="Por una nube - Award 3"
                className="w-full h-auto object-contain"
                loading="lazy"
              />
            </div>
          </motion.div>

          {/* Awards & Recognition Section - Two columns */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-center"
          >
            {/* Left Column - Text */}
            <div>
              <div className="text-foreground/90 leading-relaxed mb-6">
                <span className="font-medium text-foreground block mb-3">{t.proyectos.awardsLabel}</span>
                {t.proyectos.porUnaNubeAwards.map((award, index) => (
                  <span key={index} className="block">{award}</span>
                ))}
              </div>
              <p className="text-foreground/90 leading-relaxed">
                {t.proyectos.porUnaNubeCoprod}
              </p>
              <p className="text-foreground/90 leading-relaxed mt-4">
                {t.proyectos.porUnaNubeProducer}
              </p>
              <p className="text-foreground/90 leading-relaxed mt-4">
                {t.proyectos.porUnaNubeSearch}
              </p>
            </div>
            {/* Right Column - Image */}
            <div className="flex items-center justify-center">
              <img
                src={AWARDS_POSTER}
                alt="Por una nube - Awards"
                className="w-full h-auto rounded-sm shadow-xl"
                loading="lazy"
              />
            </div>
          </motion.div>

          {/* Kintsukuroi Section - New Feature Film */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="text-lg lg:text-xl text-foreground/80 mb-2">
              {t.proyectos.featureFilmProject}
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-foreground mb-3">
              Kintsukuroi
            </h2>
            <p className="text-base lg:text-lg text-foreground/70">
              {t.proyectos.featureFilmSciFi}
            </p>
          </motion.div>

        </div>
      </BackgroundSection>

      {/* Kintsukuroi Full-Width Poster - Minimal gap from title */}
      <section className="relative w-full -mt-16">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative w-full"
        >
          <img
            src={KINTSUKUROI_POSTER}
            alt="Kintsukuroi - Poster"
            className="w-full h-auto object-contain"
            loading="lazy"
          />
        </motion.div>
      </section>

      {/* Kintsukuroi Dossier Section - Poster RIGHT + Text LEFT - Cloud Background */}
      <BackgroundSection bgImage={FICCION_REAL_POSTER} blendTop={true} blendBottom={false} className="pb-0">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          >
            {/* LEFT COLUMN - Text */}
            <div className="order-2 lg:order-1">
              <div className="space-y-8">
                {/* Temas Section */}
                <div>
                  <h3 className="font-semibold text-foreground text-lg mb-4">{t.proyectos.themesLabel}</h3>
                  <ul className="text-foreground/80 leading-relaxed space-y-2">
                    {t.proyectos.kintsukuroiThemes.map((theme, index) => (
                      <li key={index}>• {theme}</li>
                    ))}
                  </ul>
                </div>
                
                {/* Premios Section */}
                <div>
                  <h3 className="font-semibold text-foreground text-lg mb-4">{t.proyectos.awardsLabel}</h3>
                  <ul className="text-foreground/80 leading-relaxed space-y-2">
                    {t.proyectos.kintsukuroiAwards.map((award, index) => (
                      <li key={index}>• {award}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            
            {/* RIGHT COLUMN - Poster */}
            <div className="order-1 lg:order-2 flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_21a64dd8-89a2-456a-af50-8a7c7a229e04/artifacts/h9yd5k2c_Captura%20de%20pantalla%202026-02-06%20a%20las%2015.18.17.jpg"
                alt="Kintsukuroi - Dossier Poster"
                className="w-full max-w-md h-auto rounded-sm shadow-lg"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </BackgroundSection>
    </div>
  );
};

export default ProyectosPage;
